import React from 'react';

import { Flex } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { routeParamsSel } from '@modules/location/duck/selectorsTyped';
import AdvancementDetailsContainer from '@modules/roster-lite/components/AdvancementDetails/AdvancementDetailsContainer';
import { UnitRosterContainer } from '@modules/roster-lite/components/UnitRosterPage/UnitRosterPage';
import { canViewYouthAdvancementDetailsSel } from '@modules/roster-lite/duck/selectors';
import { useYouthAdvAllowedActions } from '@modules/roster-lite/duck/useYouthAdvAllowedActions';
import type { RouteUnitRosterYouthRank } from '@modules/roster-lite/routes.types';
import { useGetPersonProfileQuery } from '@modules/rtk/esb-api';
import PageContainer from '@shared/components/Layouts/PageContainer';
import ReturnLink from '@shared/components/ReturnLink';

import { AdvancementAction } from '../AdvancementDetails/types';
import styles from './RankRequirementsPage.module.scss';
import RequirementsCard from './RequirementsCard';

const RankRequirementsContents: React.FC = () => {
  const { orgGuid, userId, rankId } = useSelector(
    routeParamsSel<RouteUnitRosterYouthRank>,
  );
  const allowed = useSelector(canViewYouthAdvancementDetailsSel);
  useGetPersonProfileQuery(
    {
      userId: Number(userId),
    },
    { skip: !userId || !orgGuid || !rankId },
  );

  // A youth never edits the rank itself, but their
  // per-rank tier does reach the requirements inside it
  const { disabledActions, requirementDisabledActions } =
    useYouthAdvAllowedActions({
      type: 'ranks',
      advancementId: rankId,
    });

  // The route thunk already 403s denied users; this guard keeps the page from
  // rendering if the component is ever mounted another way
  if (!allowed) return null;

  return (
    <PageContainer>
      <Flex vertical gap="middle">
        {orgGuid && userId && (
          <ReturnLink to={`/unit-roster/${orgGuid}/${userId}`}>
            <FormattedMessage id="unitRoster.youthRank.backToProfile" />
          </ReturnLink>
        )}
        {userId && rankId && (
          // Summary left at its content width, the checklist taking the rest, stacked below `md`
          <Flex gap="middle" align="flex-start" className={styles.columns}>
            <div className={styles.summaryCol}>
              <AdvancementDetailsContainer
                type="ranks"
                visibleActions={[AdvancementAction.CompletionDate]}
                advancementId={rankId}
                userId={userId}
                disabledActions={disabledActions}
              />
            </div>
            <div className={styles.requirementsCol}>
              <RequirementsCard
                userId={userId}
                rankId={rankId}
                disabledActions={requirementDisabledActions}
              />
            </div>
          </Flex>
        )}
      </Flex>
    </PageContainer>
  );
};

export default function RankRequirementsPage() {
  return (
    <UnitRosterContainer>
      <RankRequirementsContents />
    </UnitRosterContainer>
  );
}
